import {
  FC,
  HTMLProps,
  MutableRefObject,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getDOMProps } from 'app/shared/utils/react';
import styled, { css } from 'styled-components';

import { Dialog } from '@mui/material';
import { U21ModalContext } from 'app/shared/u21-ui/components/layout/modal/U21ModalContext';

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  FULLSCREEN: 'fullscreen',
} as const;

export type U21ModalSize = ValueOf<typeof SIZES>;

export interface U21ModalBaseProps
  extends Omit<HTMLProps<HTMLDivElement>, 'size'> {
  children: ReactNode;
  onClose?: (event: SyntheticEvent, reason: string) => void;
  onEnter?: () => void;
  onExited?: () => void;
  open: boolean;
  size?: U21ModalSize;
}

const MIN_MARGIN = 32;

const DEFAULT_MARGIN_TOP: Record<U21ModalSize, number> = {
  [SIZES.SMALL]: 120,
  [SIZES.MEDIUM]: 120,
  [SIZES.LARGE]: 120,
  [SIZES.FULLSCREEN]: MIN_MARGIN,
};

export const U21ModalBase: FC<U21ModalBaseProps> = ({
  children,
  onClose,
  onEnter,
  onExited,
  open,
  size = SIZES.MEDIUM,
  ...rest
}) => {
  const modalRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const [modalContentElement, setModalContentElement] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const modalElement = modalRef.current;

    if (
      !modalElement ||
      !modalContentElement ||
      !open ||
      // already hardcoded to MIN_MARGIN so dynamic sizing isn't needed
      size === SIZES.FULLSCREEN
    ) {
      return undefined;
    }

    const updateModalHeight = () => {
      const modalHeaderFooterHeight =
        modalElement.clientHeight - modalContentElement.clientHeight;
      const totalMarginAvailable =
        window.innerHeight -
        modalHeaderFooterHeight -
        modalContentElement.scrollHeight;

      // Calculate margin based on available space and constrain between MIN_MARGIN and default margin
      const newMarginTop = Math.min(
        DEFAULT_MARGIN_TOP[size],
        Math.max(MIN_MARGIN, totalMarginAvailable / 2),
      );

      modalElement.style.marginTop = `${newMarginTop}px`;
      modalElement.style.marginBottom = `${newMarginTop}px`;
      modalElement.style.maxHeight = `calc(100% - ${newMarginTop * 2}px)`;
    };

    const resizeObserver = new ResizeObserver(updateModalHeight);
    resizeObserver.observe(modalContentElement);
    window.addEventListener('resize', updateModalHeight, { passive: true });

    updateModalHeight();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateModalHeight);
    };
  }, [modalContentElement, open, size]);

  const u21ModalContextValue = useMemo(() => ({ setModalContentElement }), []);

  return (
    <Modal
      PaperProps={{ ref: modalRef }}
      $size={size}
      aria-labelledby="u21-modal-title"
      disableEscapeKeyDown
      onClose={onClose}
      open={open}
      TransitionProps={{ onEnter, onExited }}
      {...getDOMProps(rest)}
    >
      <U21ModalContext.Provider value={u21ModalContextValue}>
        {children}
      </U21ModalContext.Provider>
    </Modal>
  );
};

const Modal = styled(Dialog)<{ $size: U21ModalSize }>`
  .MuiDialog-container {
    align-items: stretch;

    .MuiDialog-paper {
      width: 100%;
      transition: ${(props) => props.theme.transitions.create(['all'])};
      margin: ${(props) => DEFAULT_MARGIN_TOP[props.$size]}px ${MIN_MARGIN}px;
      max-height: calc(
        100% - ${(props) => DEFAULT_MARGIN_TOP[props.$size] * 2}px
      );

      ${(props) => {
        switch (props.$size) {
          case SIZES.SMALL:
            return css`
              max-width: 540px;
              height: fit-content;
            `;
          case SIZES.MEDIUM:
            return css`
              max-width: 700px;
              height: fit-content;
            `;
          case SIZES.LARGE:
            return css`
              max-width: 1080px;
              height: fit-content;
            `;
          case SIZES.FULLSCREEN:
            return css`
              max-width: 100%;
            `;
          default:
            return css``;
        }
      }}
    }
  }
`;
